import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListItemText, MenuItem, Stack } from '@mui/material';
import { NotificationStrategyForm } from 'src/Common/Components/Forms/NotificationStrategyForm';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import {
  OrderPreparationStep,
  OrderPreparationStepType,
  StartupStrategy,
  StartupStrategyType,
  UiConfiguration,
  ValueExtender,
  Zone,
  ZoneStrategy,
} from '@ekkogmbh/apisdk';
import { StyledCheckbox } from 'src/Common/Components/Forms/StyledCheckbox';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CenteredColumn, ReactivePanelColumn, ReactiveRow } from 'src/Common/Components/Stacks';
import ListStepper from 'src/Common/Components/ListStepper';
import { MergeStrategiesConfigMap } from 'src/ZoneManagement/Helper/Strategies';
import ZoneStrategyFormSelect from 'src/ZoneManagement/Components/ZoneStrategyFormSelect';
import { RestartAlt, Settings } from '@mui/icons-material';
import StyledFormAccordion from 'src/Common/Components/Forms/StyledFormAccordion';
import useNotificationStrategy from 'src/ZoneManagement/Providers/NotificationStrategyProvider';
import RegexInput from 'src/Common/Components/Forms/RegexInput';
import { StyledSwitch } from 'src/Common/Components/Forms/StyledSwitch';
import { valueExtenderToPreparationStep } from './PickerProfilePanel';
import { enqueueSnackbar } from 'notistack';

const sortByOptions = ['created_at', 'name'].map((name: string) => (
  <MenuItem key={`sort-by-${name}`} value={name}>
    <ListItemText primary={name} />
  </MenuItem>
));
const sortDirectionOptions = ['ascending', 'descending'].map((direction: string) => (
  <MenuItem key={`sort-dir-${direction}`} value={direction}>
    <ListItemText primary={direction} />
  </MenuItem>
));
const strategyOptions = Object.values(StartupStrategyType).map((name: StartupStrategyType, index: number) => (
  <MenuItem key={index} value={name}>
    <ListItemText primary={name} />
  </MenuItem>
));

interface Props {
  zone: Zone;
  startupStrategy: StartupStrategy;
  preparationSteps?: OrderPreparationStep[];
  uiOptions: UiConfiguration['options'];
  mergeStrategy?: ZoneStrategy;
  onChangeStrategy: (startupStrategy: StartupStrategy) => void;
  onChangePreparationSteps: (preparationSteps?: OrderPreparationStep[]) => void;
  onChangeUiOptions: (options?: UiConfiguration['options']) => void;
  onChangeMergeStrategy: (strategy?: ZoneStrategy) => void;
}
export const PickerProfileStartupForm = ({
  zone,
  startupStrategy,
  preparationSteps,
  uiOptions,
  mergeStrategy,
  onChangeStrategy,
  onChangePreparationSteps,
  onChangeUiOptions,
  onChangeMergeStrategy,
}: Props): React.JSX.Element => {
  const { notificationStrategy } = useNotificationStrategy();
  const [isStartupConfigOpen, setStartupConfigOpen] = useState<boolean>(false);
  const [currentPrepIndex, setCurrentPrepIndex] = useState<number | undefined>(
    (preparationSteps?.length ?? 0) > 0 ? 0 : undefined,
  );
  const currentPreparationStep = useMemo(() => {
    if (preparationSteps !== undefined && currentPrepIndex !== undefined) {
      return preparationSteps[currentPrepIndex];
    }

    return undefined;
  }, [currentPrepIndex, preparationSteps]);

  const valueExtendersByKey = useMemo(() => {
    // find config and apply
    const allExtenders = (zone.sourceValueExtenders ?? []).concat(zone.destinationValueExtenders ?? []);
    return allExtenders.reduce((carry: Record<string, ValueExtender>, vext: ValueExtender) => {
      carry[vext.annotation] = vext;
      return carry;
    }, {});
  }, [zone]);

  /** ensure index isn't undefined when steps available */
  useEffect(() => {
    if ((preparationSteps?.length ?? 0) > 0 && currentPrepIndex === undefined) {
      setCurrentPrepIndex(0);
    }
  }, [currentPrepIndex, preparationSteps]);

  const startupForm = useMemo(() => {
    const { amount, onceOnly, sortBy, sortDirection } = startupStrategy.config;

    let configForm: React.JSX.Element;
    let hasConfig: boolean = true;

    switch (startupStrategy.name) {
      case StartupStrategyType.NEXT: {
        configForm = (
          <ReactiveRow width={'100%'}>
            <CenteredColumn width={'100%'}>
              <StyledSelectField
                label={'sort-by'}
                value={sortBy}
                onChange={(e) => {
                  onChangeStrategy({
                    ...startupStrategy,
                    config: {
                      ...startupStrategy.config,
                      sortBy: e.target.value as string,
                    },
                  });
                }}
              >
                {sortByOptions}
              </StyledSelectField>
              <StyledCheckbox
                label={'single use blueprints only'}
                value={onceOnly}
                onChange={(_e) => {
                  onChangeStrategy({
                    ...startupStrategy,
                    config: {
                      ...startupStrategy.config,
                      onceOnly: !onceOnly,
                    },
                  });
                }}
              />
            </CenteredColumn>
            <CenteredColumn width={'100%'}>
              <StyledSelectField
                label={'sort-direction'}
                value={sortDirection}
                onChange={(e) => {
                  onChangeStrategy({
                    ...startupStrategy,
                    config: {
                      ...startupStrategy.config,
                      sortDirection: e.target.value as string,
                    },
                  });
                }}
              >
                {sortDirectionOptions}
              </StyledSelectField>
              <StyledTextField
                label={'amount'}
                type={'number'}
                value={amount}
                onChange={(e) => {
                  onChangeStrategy({
                    ...startupStrategy,
                    config: {
                      ...startupStrategy.config,
                      amount: Math.max(1, Number(e.target.value)),
                    },
                  });
                }}
              />
            </CenteredColumn>
          </ReactiveRow>
        );
        break;
      }
      default:
        hasConfig = false;
        configForm = <></>;
    }

    return (
      <StyledSelectField
        label={'Startup Strategy'}
        value={startupStrategy.name}
        onChange={(e) => {
          const stratType = e.target.value as StartupStrategyType;

          let config: StartupStrategy['config'] = {};
          if (stratType === StartupStrategyType.NEXT) {
            config = {
              amount: amount ?? 1,
              onceOnly: onceOnly === true,
              sortBy: sortBy ?? 'created_at',
              sortDirection: sortDirection ?? 'descending',
            };
            setStartupConfigOpen(true);
          } else {
            setStartupConfigOpen(false);
          }
          onChangeStrategy({ name: stratType, config });
        }}
        action={{
          icon: Settings,
          onClick: () => {
            setStartupConfigOpen(!isStartupConfigOpen);
          },
          disabled: !hasConfig,
        }}
        collapsible={{
          content: configForm,
          open: isStartupConfigOpen,
        }}
      >
        {strategyOptions}
      </StyledSelectField>
    );
  }, [startupStrategy, onChangeStrategy, isStartupConfigOpen]);

  const updatePrepStep = useCallback(
    (step: OrderPreparationStep) => {
      if (currentPrepIndex === undefined || preparationSteps === undefined) {
        return;
      }
      preparationSteps[currentPrepIndex] = step;
      onChangePreparationSteps([...preparationSteps]);
    },
    [currentPrepIndex, preparationSteps, onChangePreparationSteps],
  );

  const orderPreparationForm = useMemo(() => {
    const currentKey = currentPreparationStep?.configuration.key;
    const zoneExtender = currentKey ? valueExtendersByKey[currentKey] : undefined;
    const hasValueExtender = !!zoneExtender;
    const metaFlag = currentPreparationStep?.configuration.meta;
    const isFlaggedExtender = metaFlag === 'value-extender';

    return (
      <StyledFormAccordion
        label={'Preparation Steps (optional):'}
        centerLabel={(preparationSteps?.length ?? 0) > 0 ? preparationSteps?.length.toString() : 'none'}
      >
        <ListStepper
          optional
          index={currentPrepIndex}
          length={preparationSteps?.length}
          onIndexChange={setCurrentPrepIndex}
          disableRemove={hasValueExtender}
          onAdd={() => {
            const steps = [...(preparationSteps ?? [])];
            const newLength = steps.push({
              type: OrderPreparationStepType.ANNOTATION_SCAN,
              instruction: '',
              configuration: {
                key: '',
                validationRegex: '',
                validationErrorMessage: '',
                disabled: 'false',
              },
            });
            onChangePreparationSteps(steps);
            setCurrentPrepIndex(newLength - 1);
          }}
          onRemove={() => {
            const steps = [...preparationSteps!];
            if (steps.length === 1) {
              onChangePreparationSteps(undefined);
              setCurrentPrepIndex(undefined);
            } else {
              steps.splice(currentPrepIndex!, 1);
              onChangePreparationSteps([...steps]);
            }
          }}
          onMove={(to) => {
            const steps = [...preparationSteps!];
            const targetStep = steps[to];
            steps[currentPrepIndex!] = targetStep;
            steps[to] = currentPreparationStep!;
            setCurrentPrepIndex(to);
            onChangePreparationSteps(steps);
          }}
          content={
            currentPreparationStep && (
              <Stack direction={'column'} spacing={1}>
                <ReactiveRow spacing={1}>
                  <StyledTextField
                    disabled
                    label={'Type'}
                    value={isFlaggedExtender ? metaFlag : OrderPreparationStepType.ANNOTATION_SCAN}
                    onChange={(_e) => console.log('this is disabled since there are no options yet')}
                    action={
                      hasValueExtender || isFlaggedExtender
                        ? {
                            icon: RestartAlt,
                            tooltip: 'Reset to Zone configuration',
                            onClick: () => {
                              if (zoneExtender) {
                                updatePrepStep(valueExtenderToPreparationStep(zoneExtender));
                              } else {
                                enqueueSnackbar(`Value-Extender with annotation key "${currentKey}" not found`);

                                delete currentPreparationStep.configuration.meta;
                                updatePrepStep(currentPreparationStep);
                              }
                            },
                          }
                        : undefined
                    }
                  />
                  <StyledSwitch
                    label={'disabled'}
                    value={currentPreparationStep.configuration.disabled === 'true'}
                    tooltip={'The shopfloor app will not ask for disabled preparation steps'}
                    onChange={(isOn) => {
                      currentPreparationStep.configuration.disabled = isOn ? 'true' : 'false';
                      updatePrepStep(currentPreparationStep);
                    }}
                  />
                </ReactiveRow>
                <StyledTextField
                  label={'Instruction'}
                  value={currentPreparationStep.instruction}
                  onChange={(e) => {
                    currentPreparationStep.instruction = e.target.value as string;
                    updatePrepStep(currentPreparationStep);
                  }}
                />
                <StyledTextField
                  label={'Annotation Key'}
                  value={currentPreparationStep.configuration.key}
                  disabled={hasValueExtender || isFlaggedExtender}
                  onChange={(e) => {
                    currentPreparationStep.configuration.key = e.target.value as string;
                    updatePrepStep(currentPreparationStep);
                  }}
                />
                <RegexInput
                  label={'Validation Regex'}
                  value={currentPreparationStep.configuration.validationRegex}
                  onChange={(regexString) => {
                    currentPreparationStep.configuration.validationRegex = regexString;
                    updatePrepStep(currentPreparationStep);
                  }}
                />
                <StyledTextField
                  label={'Validation Error Message'}
                  value={currentPreparationStep.configuration.validationErrorMessage}
                  onChange={(e) => {
                    const value = e.target.value as string;
                    currentPreparationStep.configuration.validationErrorMessage = value;
                    updatePrepStep(currentPreparationStep);
                  }}
                />
              </Stack>
            )
          }
        />
      </StyledFormAccordion>
    );
  }, [currentPrepIndex, preparationSteps, valueExtendersByKey, currentPreparationStep, updatePrepStep]);

  return (
    <ReactivePanelColumn lgWidth={'66%'}>
      {startupForm}
      <ZoneStrategyFormSelect
        optional
        label={'Merge Strategy (optional)'}
        strategy={mergeStrategy}
        configMap={MergeStrategiesConfigMap}
        defaultStrategy={{ name: 'append', configuration: {} }}
        onChange={(strategy) => onChangeMergeStrategy(strategy)}
      />
      <StyledFormAccordion label={'Options'}>
        <ReactiveRow>
          <StyledCheckbox
            label={'Confirm Start'}
            value={uiOptions?.confirmStart ?? false}
            onChange={(e) => {
              onChangeUiOptions({ ...uiOptions, confirmStart: e.target.checked });
            }}
            tooltip={'Prompt the picker to start the order after creating it'}
          />
          <StyledTextField
            label={'Highlight Metadata Key'}
            value={uiOptions?.highlight?.key}
            onChange={(e) => {
              if (e.target.value === '') {
                onChangeUiOptions({ confirmStart: !!uiOptions?.confirmStart });
              } else {
                onChangeUiOptions({
                  confirmStart: !!uiOptions?.confirmStart,
                  highlight: { key: e.target.value as string },
                });
              }
            }}
          />
        </ReactiveRow>
      </StyledFormAccordion>
      <StyledFormAccordion
        label={'Notification Strategy (optional):'}
        centerLabel={notificationStrategy?.name ?? 'none'}
      >
        <NotificationStrategyForm optional inheritLabel="Zone" showDisablePrevious={false} showIgnoreQueue={false} />
      </StyledFormAccordion>
      {orderPreparationForm}
    </ReactivePanelColumn>
  );
};
